import { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { Spinner, Button } from '../components/common';

interface DatabaseInfo {
  exists: boolean;
  size?: number;
  size_human?: string;
  modified_at?: string;
  environment?: string;
}

export default function AdminPage() {
  const { t } = useTranslation();
  const { user, isLoading, token } = useAuth();
  const [dbInfo, setDbInfo] = useState<DatabaseInfo | null>(null);
  const [isLoadingDb, setIsLoadingDb] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [infoError, setInfoError] = useState<string | null>(null);

  // Fetch database info
  useEffect(() => {
    if (user?.admin) {
      fetchDatabaseInfo();
    }
  }, [user?.admin]);

  const fetchDatabaseInfo = async () => {
    setIsLoadingDb(true);
    setInfoError(null);
    try {
      const response = await fetch('/admin/database/info', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setDbInfo(data);
      } else {
        let errorMsg = `HTTP ${response.status}`;
        try {
          const errorJson = await response.json();
          if (errorJson.reason) {
            errorMsg += `: ${errorJson.reason}`;
          } else if (errorJson.error) {
            errorMsg += `: ${errorJson.error}`;
          }
        } catch {
          const errorText = await response.text();
          errorMsg += `: ${errorText || response.statusText}`;
        }
        setInfoError(errorMsg);
      }
    } catch (error) {
      console.error('Failed to fetch database info:', error);
      setInfoError(error instanceof Error ? error.message : 'Failed to fetch database info');
    } finally {
      setIsLoadingDb(false);
    }
  };

  const handleDownloadDatabase = async () => {
    setIsDownloading(true);
    setDownloadError(null);
    try {
      const response = await fetch('/admin/database/download', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'database.sqlite3';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match) {
          filename = match[1];
        }
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download failed:', error);
      setDownloadError(error instanceof Error ? error.message : t('admin.downloadError'));
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  // Redirect non-admin users
  if (!user?.admin) {
    return <Navigate to="/" replace />;
  }

  const adminLinks = [
    {
      title: t('admin.backgroundJobs'),
      description: t('admin.backgroundJobsDescription'),
      href: '/jobs',
      external: true,
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
      ),
    },
    {
      title: t('admin.aiPromptLogs'),
      description: t('admin.aiPromptLogsDescription'),
      href: '/admin/ai-prompt-logs',
      external: false,
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5"
          />
        </svg>
      ),
    },
    {
      title: 'AI Evaluations',
      description: 'Quality scores for AI-generated prose and expansions.',
      href: '/admin/ai-evaluations',
      external: false,
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
          />
        </svg>
      ),
    },
    {
      title: t('admin.componentsLibrary'),
      description: t('admin.componentsLibraryDescription'),
      href: '/admin/components',
      external: false,
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
          />
        </svg>
      ),
    },
    {
      title: t('admin.errorLogs'),
      description: t('admin.errorLogsDescription'),
      href: '/admin/error-logs',
      external: false,
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
          />
        </svg>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{t('admin.title')}</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">{t('admin.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {adminLinks.map((link) => {
          const CardContent = (
            <>
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400">
                  {link.icon}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {link.title}
                  </h3>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {link.description}
                  </p>
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm text-blue-600 dark:text-blue-400">
                <span>{link.external ? t('admin.openDashboard') : t('admin.viewLogs')}</span>
                {link.external ? (
                  <svg
                    className="w-4 h-4 ml-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-4 h-4 ml-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                )}
              </div>
            </>
          );

          return link.external ? (
            <a
              key={link.href}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="block p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 hover:shadow-md transition-all duration-200"
            >
              {CardContent}
            </a>
          ) : (
            <Link
              key={link.href}
              to={link.href}
              className="block p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 hover:shadow-md transition-all duration-200"
            >
              {CardContent}
            </Link>
          );
        })}

        {/* Database Download Card */}
        <div className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg text-green-600 dark:text-green-400">
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4"
                />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('admin.databaseBackup')}
              </h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {t('admin.databaseBackupDescription')}
              </p>
            </div>
          </div>

          {/* Database Info */}
          {isLoadingDb ? (
            <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
              <Spinner size="sm" />
              <span>{t('common.loading')}</span>
            </div>
          ) : dbInfo ? (
            <div className="mt-4 space-y-1 text-sm text-gray-600 dark:text-gray-400">
              <p>
                <span className="font-medium">{t('admin.environment')}:</span>{' '}
                <span className="capitalize">{dbInfo.environment}</span>
              </p>
              {dbInfo.exists ? (
                <>
                  <p>
                    <span className="font-medium">{t('admin.databaseSize')}:</span>{' '}
                    {dbInfo.size_human}
                  </p>
                  <p>
                    <span className="font-medium">{t('admin.lastModified')}:</span>{' '}
                    {dbInfo.modified_at ? new Date(dbInfo.modified_at).toLocaleString() : '-'}
                  </p>
                </>
              ) : (
                <p className="text-amber-600 dark:text-amber-400">
                  Database file not found on server
                </p>
              )}
            </div>
          ) : (
            <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                Failed to load database info
              </p>
              {infoError && (
                <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">{infoError}</p>
              )}
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                Token present: {token ? 'Yes' : 'No'}
              </p>
            </div>
          )}

          {downloadError && (
            <div className="mt-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm font-medium text-red-700 dark:text-red-300">Download Failed</p>
              <p className="text-sm text-red-600 dark:text-red-400 mt-1">{downloadError}</p>
              <details className="mt-2 text-xs text-red-500 dark:text-red-400">
                <summary className="cursor-pointer hover:underline">Debug Info</summary>
                <ul className="mt-1 space-y-1 pl-4">
                  <li>Token present: {token ? 'Yes' : 'No'}</li>
                  <li>Token length: {token?.length || 0}</li>
                  <li>DB info loaded: {dbInfo ? 'Yes' : 'No'}</li>
                  <li>Environment: {dbInfo?.environment || 'unknown'}</li>
                </ul>
              </details>
            </div>
          )}

          <div className="mt-4">
            <Button
              onClick={handleDownloadDatabase}
              disabled={isDownloading || !dbInfo?.exists}
              isLoading={isDownloading}
              className="w-full"
            >
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              {t('admin.downloadDatabase')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
